/** Aligned with activity CallGroupType enum + AiOutboundCallStatus (API contract). */

export const DEFAULT_PAGE_SIZE = 25;

/** API value → display label for filter and badges */
export const CALL_GROUP_TYPE_OPTIONS = [
  { value: "PHONE_TASK", label: "Phone Task" },
  { value: "FAX_FAIL", label: "Fax Fail" },
  { value: "FAX_TASK", label: "Fax Task" },
  { value: "CALL", label: "Call" },
  { value: "TASK", label: "Task" },
];

const CALL_GROUP_LABEL_BY_VALUE = Object.fromEntries(
  CALL_GROUP_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function labelForCallGroupType(value) {
  if (value == null || value === "") return "—";
  return CALL_GROUP_LABEL_BY_VALUE[value] || value;
}

export const OUTBOUND_CALL_STATUSES = [
  "SCHEDULED",
  "PROCESSING",
  "CALL_INITIATED_SUCCESS",
  "CALL_INITIATED_FAILED",
  "ABORTED",
  "COMPLETED",
  "RESPONSE_PROCESSING_ERROR",
  "UNKNOWN",
];

/** MultiSelect filter options ({ id: API value, name: label }) */
export const CALL_GROUP_TYPE_MULTI_OPTIONS = CALL_GROUP_TYPE_OPTIONS.map((o) => ({
  id: o.value,
  name: o.label,
}));

export const OUTBOUND_STATUS_MULTI_OPTIONS = OUTBOUND_CALL_STATUSES.map((s) => ({
  id: s,
  name: s.replace(/_/g, " "),
}));

/**
 * Mirrors `com.razormetrics.switchservice.switches.enums.SwitchStateEnum` (`workflow.wf_item.status`).
 */
const SWITCH_WF_STATUS_IDS = [
  "DELIVERED_PRESCRIBER_FAX",
  "DECLINED",
  "QUEUED_PDF_GENERATION",
  "EXPIRED_BEFORE_FAX_SENT_TO_PRESCRIBER",
  "DECLINED_RESPONSE_FOLLOWUP",
  "FAILED_PRESCRIBER_FAX",
  "EXPIRED_AFTER_FAX_SENT_TO_PRESCRIBER",
  "ERROR_EXPIRATION_AFTER_FAX_SENT_TO_PRESCRIBER",
  "ERROR_EXPIRATION_BEFORE_FAX_SENT_TO_PRESCRIBER",
  "ERROR_DECLINE",
  "CLOSED_WHILE_EXPIRED_BY_INCOMING_RESPONSE",
  "PAW_REVIEW",
  "PROCESSING_DECLINE",
  "MISSING_PRESCRIBER_CONTACT_INFO",
  "ERROR_WORKFLOW_SETUP",
  "FAILED_PRESCRIBER_FAX_QUEUEING_IN_BUCKET",
  "QUEUED_PRESCRIBER_FAX_IN_BUCKET",
  "ERROR_PDF_GENERATION",
  "ACTIVE_DELAYED",
  "GENERATED_PDF",
  "PDF_GENERATED_HSC",
  "ON_HOLD",
  "FORWARDED_PRESCRIBER_FAX",
  "SUBMITTED_VIA_EHR",
  "DELIVERED_PHARMACY_FAX",
  "PENDING_RESPONSE_REVIEW",
  "PROCESSING_PRESCRIBER_FAX",
  "ERROR_PRESCRIBER_FAX",
  "ERROR_PUTTING_ON_HOLD",
  "PENDING_PAW_APPROVAL",
  "PENDING_PAW_REJECTION",
  "PAW_REJECTED",
  "CHECK_PAW_STATE",
  "CHECK_IF_PDF_EXISTS",
  "CUSTOM_SWITCH_READY",
  "CHECK_CONTACT_INFO",
  "PDF_READY",
  "SUCCESSFUL_PDF_GENERATION",
  "ERROR_SET_ACTIVE_DELAYED",
  "ERROR_SET_PDF_GENERATION",
  "NO_PRESCRIBER_RESPONSE",
  "ERROR_PUTTING_ON_NO_PRESCRIBER_RESPONSE",
  "NO_PRESCRIBER_CONTACT",
  "ERROR_PUTTING_ON_PRESCRIBER_CONTACT",
  "RECEIVED_PRESCRIBER_FAX",
  "ACCEPTED_RESPONSE_FOLLOWUP",
  "ERROR_RESPONSE_FOLLOWUP",
  "QUEUED_PHARMACY_FAX_IN_BUCKET",
  "FAILED_PHARMACY_FAX_QUEUEING_IN_BUCKET",
  "ACCEPTED_PDF_READY",
  "WAITING_FOR_PHARMACY_FAX_NUM",
  "FORWARDED_PHARMACY_FAX",
  "FAILED_PHARMACY_FAX",
  "ERROR_PHARMACY_FAX",
  "CLOSED_AS_NON_FORMULARY_TARGET",
  "QUEUED_SMS",
  "FORWARDED_SMS",
  "ERROR_SMS",
  "DELIVERED_SMS",
  "FAILED_SMS",
  "ERROR_SUBMITTED_VIA_EHR",
  "COMPLETED",
  "CLOSED_BY_RESPONSE_ON_EXPIRED_SWITCH",
  "CLOSED_WHILE_EXPIRED_DUE_TO_NO_PRESCRIBER_RESPONSE",
  "CLOSED_WHILE_EXPIRED_DUE_TO_NO_PRESCRIBER_CONTACT",
  "CLOSED_BY_DUPLICATE_SWITCH",
  "OPEN_FILL",
  "REFUND_SOFT_FILL",
  "QUEUED_PHARMACY_FAX",
  "QUEUED_PRESCRIBER_FAX",
  "QUEUED_PDF_GENERATION_ON_RESEND_FAX",
  "GENERATED_PDF_ON_RESEND_FAX",
  "ERROR_PDF_GENERATION_ON_RESEND_FAX",
  "CHECK_SWITCH_EXPIRY",
  "PRESCRIBER_OPT_OUT",
  "ERROR_PUTTING_ON_PRESCRIBER_OPT_OUT",
  "MEMBER_OPT_OUT",
  "ERROR_PUTTING_ON_MEMBER_OPT_OUT",
  "NINTY_DAY_CLOSE_MISSING_DATA",
  "ERROR_PUTTING_ON_NINTY_DAY_CLOSE",
  "FORMULARY_PLAN_ISSUE",
  "ERROR_PUTTING_ON_FORMULARY_PLAN_ISSUE",
  "PRESCRIBER_FAX_PHONE_NUMBER_CHANGED",
  "PHARMACY_FAX_PHONE_NUMBER_CHANGED",
  "MEMBER_PHONE_NUMBER_CHANGED",
  "PROCESSING_SUBMIT_VIA_EHR",
  "CLOSED_DUE_TO_PREMATURE_FILL",
  "ERROR_ON_CLOSED_DUE_TO_PREMATURE_FILL",
  "CLOSED",
  "PROCESSING_MEMBER_ON_TARGET",
  "ERROR_MEMBER_ON_TARGET",
  "TARGET_NOT_FILLED",
  "ERROR_TARGET_NOT_FILLED",
  "MEMBER_NO_LONGER_ON_PLAN",
  "ERROR_MEMBER_NO_LONGER_ON_PLAN",
  "NEGATIVE_FILL_SAVINGS",
  "ERROR_NEGATIVE_FILL_SAVINGS",
];

export const SWITCH_WF_STATUS_MULTI_OPTIONS = SWITCH_WF_STATUS_IDS.map((id) => ({
  id,
  name: id.replace(/_/g, " "),
}));

/**
 * Canonical tag strings — mirrors `AiOutComeTagEnum` (`activityservice.aivendor.enums`).
 * Used for filters and row display labels.
 */
const OUTCOME_TAG_IDS = [
  "call_back_later",
  "fax_not_received",
  "fax_received",
  "fax_verified",
  "human_requested",
  "member_not_on_plan",
  "member_office_visit",
  "member_verbal_decline",
  "new_fax_provided",
  "new_phone_provided",
  "no_fax_machine",
  "opt_out_permanent",
  "patient_not_with_practice",
  "phone_verified",
  "provider_not_at_practice",
  "provider_retired",
  "provider_unknown",
  "provider_verbal_decline",
  "refused_to_help",
  "sent_ehr",
  "response_processing",
  "do_not_call_only",
  "continue_therapy",
  "stop_faxing_switch",
  "do_not_change",
  "patient_failed",
  "patient_refused",
  "not_appropriate_switch",
  "therapy_discontinued",
  "other_reason",
];

function titleCaseWords(s) {
  return s
    .split("_")
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

export const OUTCOME_TAG_MULTI_OPTIONS = OUTCOME_TAG_IDS.map((id) => ({
  id,
  name: titleCaseWords(id),
}));

const OUTCOME_TAG_LABEL_BY_ID = Object.fromEntries(
  OUTCOME_TAG_MULTI_OPTIONS.map((o) => [o.id, o.name])
);

export function labelForOutcomeTag(value) {
  if (value == null || value === "") return "—";
  return OUTCOME_TAG_LABEL_BY_ID[value] || titleCaseWords(String(value));
}
